/** Merge notification API `data` and `deepLink` maps (same rules as mobile AppNotification handling). */
export function mergeNotificationPayload(notification) {
  const raw = notification && typeof notification === "object" ? notification : {};
  const data =
    raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : {};
  const deepLink =
    raw.deepLink &&
    typeof raw.deepLink === "object" &&
    !Array.isArray(raw.deepLink)
      ? raw.deepLink
      : {};
  const merged = { ...data, ...deepLink };
  // Some APIs put deep-link ids on the root of the notification object
  if (raw.conversationId != null) merged.conversationId = raw.conversationId;
  if (raw.conversation_id != null)
    merged.conversationId = raw.conversation_id;
  return merged;
}

export function normalizeNotificationsResponse(body) {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (typeof body === "object") {
    if (Array.isArray(body.items)) return body.items;
    if (Array.isArray(body.notifications)) return body.notifications;
  }
  return [];
}

export function isUnreadNotification(notification) {
  if (!notification || typeof notification !== "object") return false;
  const status = (notification?.status ?? "").toString().toLowerCase();
  const read =
    notification?.isRead === true ||
    notification?.read === true ||
    notification?.seen === true ||
    notification?.readAt != null ||
    notification?.seenAt != null ||
    status === "read";
  return !read;
}

export function isMessageNotification(notification) {
  if (!notification || typeof notification !== "object") return false;

  const payload = mergeNotificationPayload(notification);
  const screen = payload?.screen?.toString().toLowerCase() ?? "";
  if (screen === "messages") return true;

  const type = (notification.type ?? notification.notificationType ?? "")
    .toString()
    .toLowerCase();
  const title = (notification.title ?? notification.subject ?? "")
    .toString()
    .toLowerCase();
  const body = (
    notification.body ??
    notification.message ??
    notification.content ??
    notification.text ??
    ""
  )
    .toString()
    .toLowerCase();

  const conversationId =
    payload?.conversationId ?? payload?.conversation_id ?? null;

  if (conversationId != null) return true;
  if (
    type.includes("message") ||
    type.includes("conversation") ||
    type.includes("chat")
  ) {
    return true;
  }

  const haystack = `${title} ${body}`;
  return (
    haystack.includes("message") ||
    haystack.includes("chat") ||
    haystack.includes("conversation")
  );
}

export function isCommentNotification(notification) {
  if (!notification || typeof notification !== "object") return false;
  if (isMessageNotification(notification)) return false;

  const payload = mergeNotificationPayload(notification);
  const screen = payload?.screen?.toString().toLowerCase() ?? "";
  if (screen === "logworkout" && payload?.workoutId != null) return true;

  const type = (notification.type ?? notification.notificationType ?? "")
    .toString()
    .toLowerCase();
  if (type === "comment" || type.includes("comment")) return true;

  const title = (notification.title ?? notification.subject ?? "")
    .toString()
    .toLowerCase();
  const body = (
    notification.body ??
    notification.message ??
    notification.content ??
    notification.text ??
    ""
  )
    .toString()
    .toLowerCase();
  const haystack = `${title} ${body}`;
  return haystack.includes("comment") || haystack.includes("reply");
}

export function notificationMatchesConversation(notification, conversationId) {
  if (conversationId == null || conversationId === "") return false;
  const payload = mergeNotificationPayload(notification);
  const id = payload?.conversationId ?? payload?.conversation_id;
  return id != null && String(id) === String(conversationId);
}

export function notificationMatchesWorkoutComment(
  notification,
  workoutId,
  exerciseIndex
) {
  if (workoutId == null || workoutId === "") return false;
  const payload = mergeNotificationPayload(notification);
  if (String(payload.workoutId ?? "") !== String(workoutId)) return false;
  if (exerciseIndex != null && exerciseIndex !== undefined) {
    return String(payload.exerciseIndex ?? "") === String(exerciseIndex);
  }
  return isCommentNotification(notification);
}

export const NOTIFICATIONS_CHANGED_EVENT = "trainer-notifications-changed";

export function dispatchNotificationsChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED_EVENT));
  }
}
